import { useRef, type RefObject, useEffect } from 'react';

type DraggableOptions = {
    dragThreshold?: number;
    currentPage?: number;
    goToPage?: (page: number) => void;
    pageCount?: number;
};

export const useDraggable = (ref: RefObject<HTMLElement | null>, options?: DraggableOptions) => {
    const { dragThreshold = 30, currentPage: activePage, goToPage, pageCount } = options || {};

    const activePageRef = useRef(activePage);
    const goToPageRef = useRef(goToPage);
    const coords = useRef({
        didDragStart: false,
        startX: 0,
        initialX: 0,
        initialScrollLeft: 0,
        totalDragDistance: 0,
        startingPage: 0,
        isMouseDrag: false
    });

    useEffect(() => {
        activePageRef.current = activePage;
        goToPageRef.current = goToPage;
    }, [activePage, goToPage]);

    const handleDrag = (clientX: number) => {
        if (!coords.current.didDragStart || !ref.current) {
            return;
        }
        if (coords.current.startX === clientX) {
            return;
        }

        ref.current.classList.add('dragging');
        const draggingDistance = coords.current.startX - clientX;
        coords.current.totalDragDistance += draggingDistance;
        coords.current.startX = clientX;

        if (coords.current.isMouseDrag) {
            const scrollLeft = coords.current.initialScrollLeft + (coords.current.initialX - clientX);
            ref.current.scrollLeft = scrollLeft;
        }
    };

    const handleMouseDrag = (e: MouseEvent) => {
        handleDrag(e.clientX);
    };

    const handleTouchDrag = (e: TouchEvent) => {
        if (e.touches.length > 0) {
            handleDrag(e.touches[0].clientX);
        }
    };

    const resetDragState = () => {
        coords.current.didDragStart = false;
        coords.current.startX = 0;
        coords.current.initialX = 0;
        coords.current.initialScrollLeft = 0;
        coords.current.totalDragDistance = 0;
        coords.current.startingPage = 0;
        coords.current.isMouseDrag = false;
        ref?.current?.classList?.remove('dragging');
    };

    const handleDragEnd = () => {
        const totalDistance = coords.current.totalDragDistance;
        const currentActivePage = activePageRef.current;
        const currentGoToPage = goToPageRef.current;

        if (ref.current) {
            ref.current.style.userSelect = '';
            ref.current.style.cursor = '';
        }

        if (Math.abs(totalDistance) >= dragThreshold) {
            ref.current?.dispatchEvent(new CustomEvent('draggable-swipe', { bubbles: true }));

            if (currentGoToPage !== undefined && currentActivePage !== undefined) {
                if (currentActivePage !== coords.current.startingPage) {
                    currentGoToPage(currentActivePage);
                } else {
                    const direction = totalDistance > 0 ? 1 : -1;
                    let targetPage = coords.current.startingPage + direction;
                    if (pageCount !== undefined) {
                        targetPage = Math.max(0, Math.min(pageCount - 1, targetPage));
                    }
                    currentGoToPage(targetPage);
                }
            }
        }

        resetDragState();
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('mousemove', handleMouseDrag);
        window.removeEventListener('touchend', handleDragEnd);
        window.removeEventListener('touchmove', handleTouchDrag);
    };

    const handleDragStart = (e: React.MouseEvent<HTMLElement>) => {
        if (!ref.current) {
            return;
        }
        coords.current.didDragStart = true;
        coords.current.isMouseDrag = true;
        coords.current.startX = e.clientX;
        coords.current.initialX = e.clientX;
        coords.current.initialScrollLeft = ref.current.scrollLeft;
        coords.current.totalDragDistance = 0;
        coords.current.startingPage = activePageRef.current ?? 0;

        e.preventDefault();
        ref.current.style.userSelect = 'none';
        ref.current.style.cursor = 'grabbing';

        window.addEventListener('mouseup', handleDragEnd, { passive: true });
        window.addEventListener('mousemove', handleMouseDrag, { passive: true });
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
        if (e.touches.length > 0 && ref.current) {
            coords.current.didDragStart = true;
            coords.current.isMouseDrag = false;
            coords.current.startX = e.touches[0].clientX;
            coords.current.initialX = e.touches[0].clientX;
            coords.current.totalDragDistance = 0;
            coords.current.startingPage = activePageRef.current ?? 0;
            window.addEventListener('touchend', handleDragEnd, { passive: true });
            window.addEventListener('touchmove', handleTouchDrag, { passive: true });
        }
    };

    return {
        handleDragStart,
        handleDragEnd,
        handleTouchStart
    };
};
